/**
 * What this tab is holding, and letting go of what it is not (RFC 0317).
 *
 * Two halves. The tab repo REPORTS the documents it has handles for to the
 * shared repo on an interval, so the worker can evict what no tab reports
 * (§3b, the worker side). And every reader HOLDS what it reads (§3d): the
 * document hooks for as long as they are mounted, a reader outside React for
 * as long as it keeps the handle. When the last holder releases a document
 * the handle is evicted from the tab repo, so the second wasm instance stops
 * growing and the next report to the worker no longer names it.
 *
 * A document nothing ever held (a one-off `repo.find` that read it and moved
 * on) is caught by the sweep: loaded, unheld, for `idleMs`, it goes the same
 * way. Measured before the sweep (Oct 7): 266 documents loaded, 6 held.
 *
 * The release is delayed a little: a pane that re-mounts, or a session that
 * moves from one pane to another, should not pay a reload for the gap.
 */

import {
  type AnyDocumentId,
  interpretAsDocumentId,
  type Repo,
} from '@automerge/automerge-repo/slim'
import { evictDocuments } from '@mod/core/core'

/** A held document released by its last holder waits this long before eviction. */
export const RELEASE_GRACE_MS = 2_000
/** A loaded document nothing has held for this long is swept. */
export const UNHELD_IDLE_MS = 2 * 60_000

export interface HeldDocs {
  /** A reader has the document open. Returns the release. */
  hold(id: string): () => void
  /** Every document the tab repo currently has a handle for. */
  loaded(): string[]
  /** Documents held right now (for tests and the report). */
  holders(id: string): number
  /** Evict what has been loaded and unheld for `idleMs`. Returns what went. */
  sweep(): Promise<string[]>
}

const registry = new WeakMap<Repo, HeldDocs>()

/**
 * Hold a document from outside React, for a reader that keeps a handle past
 * one read: a watcher, a subscription. A no-op on a repo with no held set
 * (the main-thread repo, a test repo).
 */
export function holdDocument(repo: Repo, id: string): () => void {
  const held = registry.get(repo)
  if (!held) return () => {}
  try {
    return held.hold(id)
  } catch {
    // Not a document id: there is nothing to hold.
    return () => {}
  }
}

export function createHeldDocs(
  repo: Repo,
  opts: {
    graceMs?: number
    idleMs?: number
    now?: () => number
    /**
     * Called after documents leave the tab repo. The tab repo has no storage,
     * so the ONLY way one comes back is a fresh sync from the shared repo, and
     * the shared repo will not start one while its sync state says this tab
     * has every head. The caller tells the worker to forget (`released`).
     */
    onEvict?: (ids: string[]) => void
  } = {},
): HeldDocs {
  const counts = new Map<string, number>()
  const pending = new Map<string, ReturnType<typeof setTimeout>>()
  /** When each loaded, unheld document was first seen that way. */
  const unheldSince = new Map<string, number>()
  const grace = opts.graceMs ?? RELEASE_GRACE_MS
  const idleMs = opts.idleMs ?? UNHELD_IDLE_MS
  const now = opts.now ?? Date.now

  const handles = () =>
    (
      repo as unknown as {
        handles?: Record<string, { isReady?: () => boolean }>
      }
    ).handles ?? {}
  const loaded = (): string[] => Object.keys(handles())

  const evict = async (ids: string[]): Promise<string[]> => {
    const out = ids.filter((id) => (counts.get(id) ?? 0) === 0)
    if (out.length === 0) return []
    for (const id of out) unheldSince.delete(id)
    await evictDocuments(repo, out)
    opts.onEvict?.(out)
    return out
  }

  const held: HeldDocs = {
    hold(id) {
      // Repo's cache is keyed by document ID, not the URL a reader used.
      id = interpretAsDocumentId(id as AnyDocumentId)
      const t = pending.get(id)
      if (t) {
        clearTimeout(t)
        pending.delete(id)
      }
      unheldSince.delete(id)
      counts.set(id, (counts.get(id) ?? 0) + 1)
      let released = false
      return () => {
        if (released) return
        released = true
        const n = (counts.get(id) ?? 1) - 1
        if (n > 0) {
          counts.set(id, n)
          return
        }
        counts.delete(id)
        pending.set(
          id,
          setTimeout(() => {
            pending.delete(id)
            void evict([id])
          }, grace),
        )
      }
    },
    loaded,
    holders(id) {
      return counts.get(interpretAsDocumentId(id as AnyDocumentId)) ?? 0
    },
    async sweep() {
      const at = now()
      const due: string[] = []
      const seen = new Set<string>()
      for (const [id, handle] of Object.entries(handles())) {
        seen.add(id)
        if ((counts.get(id) ?? 0) > 0 || pending.has(id)) continue
        // Still loading: its finder is waiting on it, and it weighs nothing.
        if (handle.isReady?.() === false) continue
        const since = unheldSince.get(id)
        // First seen unheld: start its clock, so a fresh load is never swept
        // in the same tick as the find that loaded it.
        if (since === undefined) unheldSince.set(id, at)
        else if (at - since >= idleMs) due.push(id)
      }
      for (const id of unheldSince.keys()) {
        if (!seen.has(id)) unheldSince.delete(id)
      }
      return evict(due)
    },
  }
  registry.set(repo, held)
  return held
}

/**
 * A repo whose finds hold what they load until `release`, for a caller that
 * keeps the handles it finds and lives in code that cannot import this file
 * (core's voice call). Everything else passes through to the repo itself.
 */
export function holdingRepo(repo: Repo): { repo: Repo; release(): void } {
  const releases: (() => void)[] = []
  const holding =
    (name: 'find' | 'findWithProgress') =>
    (id: AnyDocumentId, ...rest: unknown[]) => {
      releases.push(holdDocument(repo, String(id)))
      return (repo[name] as (...args: unknown[]) => unknown).call(
        repo,
        id,
        ...rest,
      )
    }
  const find = holding('find')
  const findWithProgress = holding('findWithProgress')
  const proxy = new Proxy(repo, {
    get(target, prop) {
      if (prop === 'find') return find
      if (prop === 'findWithProgress') return findWithProgress
      const value = Reflect.get(target, prop, target)
      // Repo keeps private fields, so its methods must run on the repo.
      return typeof value === 'function' ? value.bind(target) : value
    },
  })
  return {
    repo: proxy,
    release() {
      for (const release of releases.splice(0)) release()
    },
  }
}

/**
 * Ask back for a document this repo let go of, when something finds it again
 * (RFC 0317 3d).
 *
 * The tab repo has no storage, so an evicted document can only come back by
 * the worker sending it again. The worker stops sending it on `released`, or
 * every eviction came straight back (seen live, Oct 7). And the repo's own
 * find of it sends nothing (seen live, 2026-09-19). So a find of an evicted
 * id calls `onWant` once, and the worker sends it whole. See
 * evict-refind.test.ts in core. Returns the function that marks ids evicted.
 */
export function askBackOnFind(
  repo: Repo,
  onWant: (id: string) => void,
): (ids: readonly string[]) => void {
  const evicted = new Set<string>()
  const want = (id: unknown): void => {
    let documentId: string
    try {
      documentId = interpretAsDocumentId(id as AnyDocumentId)
    } catch {
      return
    }
    if (evicted.delete(documentId)) onWant(documentId)
  }
  const find = repo.find.bind(repo)
  const findWithProgress = repo.findWithProgress.bind(repo)
  repo.find = ((id, ...rest) => {
    want(id)
    return find(id, ...rest)
  }) as typeof repo.find
  repo.findWithProgress = ((id, ...rest) => {
    want(id)
    return findWithProgress(id, ...rest)
  }) as typeof repo.findWithProgress
  return (ids) => {
    for (const id of ids) evicted.add(id)
  }
}
